import { useState } from 'react';
import { TableRow, TableCell, MenuItem, Stack, IconButton, Box, Tooltip } from '@mui/material';
// @types
import { TableMoreMenu } from 'src/components/table';
import Iconify from 'src/components/Iconify';
import { MCQQuestion, QuestionAnswer } from 'src/@types/Course';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';

type Props = {
  row: any;
  selected: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  onMoveUp: VoidFunction;
  onMoveDown: VoidFunction;
  // First / last in the section's REAL lesson order (not the visible page).
  isFirst: boolean;
  isLast: boolean;
  // Hidden while a title search is on, on a partner course, or while lesson problems wait for a fix.
  showArrows: boolean;
  // A save is running: arrows and the row menu wait for it.
  disabled: boolean;
  // Partner course: no Edit / Delete.
  readOnly: boolean;
};

export default function SectionTableItemRow({
  row,
  selected,
  onEditRow,
  onDeleteRow,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  showArrows,
  disabled,
  readOnly,
}: Props) {
  const { title, details, points, duration } = row;
  // A lesson without an id cannot be addressed until "Fix lesson problems".
  const addressable = typeof row?.uuid === 'string' && row.uuid !== '';
  const name = title || row?.title_ar || 'lesson';

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const getPoints = () => {
    let point = 0;
    row?.details?.questions?.forEach((quistion: MCQQuestion) => {
      quistion?.answers?.forEach((answer: QuestionAnswer) => {
        if (answer.correct) point = point + 1;
      });
    });
    return point;
  };

  const arrow = (direction: 'up' | 'down') => {
    const hidden = direction === 'up' ? isFirst : isLast;
    if (hidden) return <Box key={direction} sx={{ width: 40 }} />;
    return (
      <Tooltip key={direction} title={`Move ${direction}`}>
        <span>
          <IconButton
            size="small"
            aria-label={`Move ${name} ${direction}`}
            disabled={disabled || !addressable}
            onClick={direction === 'up' ? onMoveUp : onMoveDown}
          >
            {direction === 'up' ? <ArrowCircleUpIcon /> : <ArrowCircleDownIcon />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <TableRow hover selected={selected} data-lesson-uuid={row?.uuid || ''}>
      <TableCell align="left"> {title}</TableCell>
      <TableCell align="left"> {details?.type}</TableCell>
      <TableCell align="left"> {points ?? getPoints()}</TableCell>
      <TableCell align="left"> {duration}</TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" justifyContent={'end'} spacing={1}>
          {showArrows && (
            <>
              {arrow('up')}
              {arrow('down')}
            </>
          )}
          {!readOnly && (
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              actions={
                <>
                  <MenuItem
                    disabled={disabled || !addressable}
                    onClick={() => {
                      onEditRow();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:edit-fill'} />
                    Edit
                  </MenuItem>
                  <MenuItem
                    disabled={disabled || !addressable}
                    onClick={() => {
                      onDeleteRow();
                      handleCloseMenu();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Iconify icon={'eva:trash-2-outline'} />
                    Delete
                  </MenuItem>
                </>
              }
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
